import React from "react";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Calendar, Clock, Phone, Shield, Star, CheckCircle, CreditCard } from "lucide-react";
import { Link } from "gatsby";
import BookingLeadForm from "@/components/BookingLeadForm";
import TelLink from "@/components/TelLink";

const BookingPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-hero py-16 pt-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-4">
                Book Your Dental Appointment
              </h1>
              <p className="text-lg text-neutral-800 mb-6">
                Quick 2-minute booking — no credit card required. We'll confirm your appointment promptly.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-4">
                <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2">
                  <Calendar className="w-5 h-5 text-primary mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Same-day emergency slots available</span>
                </div>
                <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2">
                  <Clock className="w-5 h-5 text-primary mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Open Mon–Sat</span>
                </div>
                <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2">
                  <Phone className="w-5 h-5 text-primary mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Instant confirmation sent</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-8 bg-white border-b border-primary/10">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: CheckCircle, text: "CDBS Kids Dental Accepted" },
                { icon: Shield, text: "All Health Funds Accepted" },
                { icon: Star, text: "5-Star Google Rated" },
                { icon: CreditCard, text: "Interest-Free Plans Available" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-sm text-neutral-700">
                  <Icon className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="font-medium">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Booking Form Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <Card className="border-0 shadow-soft hover:shadow-medium transition-smooth">
                <CardHeader>
                  <h2 className="text-2xl font-bold text-neutral-800 text-center mb-2">
                    Request an Appointment
                  </h2>
                  <p className="text-sm text-neutral-700 text-center">
                    Complete the form below and we'll send you a link to our live booking system. Prefer to call? Contact us on{" "}
                    <TelLink clickSource="booking_page_inline" className="text-primary font-semibold hover:underline">
                      03 9022 4442
                    </TelLink>
                  </p>
                </CardHeader>
                <CardContent className="p-6 md:p-10">
                  {/* Booking Form */}
                  <div className="max-w-xl mx-auto">
                    <BookingLeadForm />
                  </div>
                  
                  <p className="text-xs text-neutral-500 text-center mt-6">
                    By submitting your details, you agree to our{" "}
                    <Link to="/privacy-policy" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-0 shadow-soft text-center">
                  <CardContent className="pt-6">
                    <Calendar className="w-10 h-10 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-neutral-800 mb-2">Flexible Scheduling</h3>
                    <p className="text-sm text-neutral-600">
                      Choose from available time slots that fit your schedule
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-soft text-center">
                  <CardContent className="pt-6">
                    <Clock className="w-10 h-10 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-neutral-800 mb-2">Same Day Available</h3>
                    <p className="text-sm text-neutral-600">
                      Emergency appointments available for urgent dental care
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-soft text-center">
                  <CardContent className="pt-6">
                    <Phone className="w-10 h-10 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-neutral-800 mb-2">Instant Confirmation</h3>
                    <p className="text-sm text-neutral-600">
                      Receive confirmation via SMS and email immediately
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BookingPage;
